"use client";

import { cn } from "@/lib/utils";
import {
  DEFAULT_ALERT_DESCRIPTION,
  DEFAULT_ALERT_LEVEL,
  DEFAULT_ALERT_TITLE,
  getSystemAlertLevelStyles,
  type AlertLevel,
} from "@/lib/systemAlert";

interface SystemAlertBannerProps {
  alert?: {
    enabled: boolean;
    title?: string | null;
    description?: string | null;
    level?: string | null;
  } | null;
}

export function SystemAlertBanner({ alert }: SystemAlertBannerProps) {
  if (!alert?.enabled) {
    return null;
  }

  const level: AlertLevel =
    alert.level === "info" || alert.level === "warning" || alert.level === "critical"
      ? alert.level
      : DEFAULT_ALERT_LEVEL;

  const styles = getSystemAlertLevelStyles(level);
  const title = alert.title?.trim() || DEFAULT_ALERT_TITLE;
  const description = alert.description?.trim() || DEFAULT_ALERT_DESCRIPTION;

  return (
    <div className={cn("border-b", styles.barBorder, styles.barBg)}>
      <div className="mx-auto flex min-h-12 max-w-screen-2xl items-center justify-center px-4 py-2.5 sm:px-6 sm:py-3 lg:px-8">
        <div className="flex w-full max-w-3xl flex-col items-center justify-center gap-1.5 text-center sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-2.5 sm:gap-y-1">
          <span
            className={cn(
              "inline-flex h-2.5 w-2.5 flex-shrink-0 animate-pulse rounded-full",
              styles.pulseDot
            )}
            aria-hidden="true"
          />
          <span
            className={cn(
              "text-[0.72rem] font-medium uppercase tracking-[0.18em]",
              styles.barDescriptionText
            )}
          >
            {styles.prefix}
          </span>
          <span className={cn("hidden text-xs opacity-45 sm:inline", styles.barDescriptionText)}>
            •
          </span>
          <span
            className={cn("text-sm font-semibold leading-snug sm:text-[0.95rem]", styles.barText)}
          >
            {title}
          </span>
          {description && (
            <span className={cn("hidden text-xs opacity-45 sm:inline", styles.barDescriptionText)}>
              •
            </span>
          )}
          {description && (
            <span className={cn("text-xs leading-relaxed sm:text-sm", styles.barDescriptionText)}>
              {description}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
